/**
 * Provisional reference of a draft invoice.
 *
 * A draft carries no fiscal number: its `number` holds `BROUILLON-<id>`,
 * derived from the invoice id, until the fiscal number is drawn at issue.
 * The id never changes, so the reference stays attached to the issued
 * invoice. It never matches the fiscal shape `[PREFIX-][TYPE-]YYYY-NNNNNN`.
 */
/** Head of a provisional reference. */
export const DRAFT_REFERENCE_PREFIX = "BROUILLON-";
/** Length of the short code a provisional reference is shown and searched by. */
export const DRAFT_REFERENCE_CODE_LENGTH = 8;
/** Provisional reference of the draft whose id is `invoiceId`. */
export function draftReference(invoiceId) {
    return `${DRAFT_REFERENCE_PREFIX}${invoiceId}`;
}
/** True when `number` is a provisional reference, not a fiscal number. */
export function isDraftReference(number) {
    return number.startsWith(DRAFT_REFERENCE_PREFIX);
}
/** Short code of a provisional reference: the first characters of the id, upper case. */
export function draftReferenceCode(number) {
    return number
        .slice(DRAFT_REFERENCE_PREFIX.length, DRAFT_REFERENCE_PREFIX.length + DRAFT_REFERENCE_CODE_LENGTH)
        .toUpperCase();
}
/**
 * How an invoice is named on screen and in messages: « Brouillon » and the
 * short code for a draft, the fiscal number otherwise.
 */
export function invoiceReferenceLabel(number) {
    return isDraftReference(number) ? `Brouillon ${draftReferenceCode(number)}` : number;
}
/** File-name stem of an invoice: `Brouillon-<code>` for a draft, the fiscal number otherwise. */
export function invoiceReferenceFileStem(number) {
    return isDraftReference(number) ? `Brouillon-${draftReferenceCode(number)}` : number;
}
/**
 * Lower-case id prefix a search term designates through a provisional
 * reference (`BROUILLON-3f2a9c1e…`, `Brouillon 3F2A9C1E`, or the bare short
 * code), or null when the term is not shaped like one.
 */
export function draftReferenceSearchPrefix(term) {
    const body = term.trim().replace(/^brouillon[\s-]*/i, "").toLowerCase();
    return new RegExp(`^[0-9a-f]{${DRAFT_REFERENCE_CODE_LENGTH}}(?:-[0-9a-f-]*)?$`).test(body) ? body : null;
}
//# sourceMappingURL=draft-reference.js.map